import { TreeViewExpandChangeEvent } from '@progress/kendo-react-treeview';
import { ModulePrivileges } from '../gql/graphql';
import { IAdminMenuItem } from './AdminMenuItem';

// This menu data structure is the default value for the menu. It contains/defines which privileges are necessary to see a page
export const menu: IAdminMenuItem[] = [
  {
    text: 'Einstellungen',
    expanded: true,
    icon: 'Cog6Tooth',
    items: [
      {
        text: 'Funktion',
        href: '/admin/function',
        icon: 'AdjustmentsHorizontal',
        privilege: ModulePrivileges.Company,
      },
      {
        text: 'Darstellung',
        href: '/admin/appearance',
        icon: 'Swatch',
        privilege: ModulePrivileges.Company,
      },
      {
        text: 'Inhaltsblöcke',
        href: '/admin/public-seminar-page-fields',
        icon: 'PresentationChartBar',
        privilege: ModulePrivileges.PublicSeminarPageFields,
      },

      {
        text: 'E-Mail',
        href: '/admin/mailsettings',
        icon: 'Envelope',
        privilege: ModulePrivileges.Company,
      },
      {
        text: 'Anmeldeformulare',
        href: '/admin/sign-up-forms',
        icon: 'Identification',
        privilege: ModulePrivileges.SignUpForm,
      },
      {
        text: 'Anmeldegruppen',
        href: '/admin/sign-up-form-select',
        icon: 'BuildingOffice',
        privilege: ModulePrivileges.SignUpForm,
      },
    ],
  },
  {
    text: 'Stammdaten',
    icon: 'Key',
    expanded: true,
    items: [
      {
        text: 'Seiten',
        href: '/admin/sites',
        icon: 'Newspaper',
        privilege: ModulePrivileges.Site,
      },
      {
        text: 'Kategorien',
        href: '/admin/categories',
        icon: 'Tag',
        privilege: ModulePrivileges.Categories,
      },
      {
        text: 'Flags',
        href: '/admin/flags',
        icon: 'Flag',
        privilege: ModulePrivileges.Flags,
      },
    ],
  },
  {
    text: 'Personen',
    icon: 'UserCircle',
    expanded: true,
    items: [
      {
        text: 'Alle Personen',
        href: '/admin/users',
        icon: 'UserGroup',
        privilege: ModulePrivileges.User,
      },
    ],
  },
  {
    text: 'Seminare',
    icon: 'BookOpen',
    expanded: true,
    items: [
      {
        text: 'Alle Seminare',
        href: '/admin/seminars',
        icon: 'ListBullet',
        privilege: ModulePrivileges.Seminar,
      },
      {
        text: 'Kalender',
        href: '/admin/calendar',
        icon: 'CalendarDays',
        privilege: ModulePrivileges.Company,
      },
      {
        text: 'Anmeldungen',
        href: '/admin/export-signups',
        icon: 'ArrowDownTray',
        privilege: ModulePrivileges.Company,
      },
    ],
  },
  {
    text: 'Support',
    icon: 'Lifebuoy',
    expanded: true,
    items: [
      {
        text: 'E-Learning',
        href: 'https://elearning.seminar-anmelden.de/login',
        icon: 'CalendarDays',
        privilege: ModulePrivileges.Member,
        newTab: true,
      },
      {
        text: 'BTS Webseite',
        href: 'https://bts.cosh.de/',
        icon: 'GlobeEuropeAfrica',
        privilege: ModulePrivileges.Member,
        newTab: true,
      },
      {
        text: 'Make a Wish',
        icon: 'Sparkles',
        action: true,
      },
    ],
  },
];

export const getMenuItemBySlug = (slug: string) =>
  findInMenuTree((item) => item.href === slug, menu);

// We apply a set of given privileges for a user to the default menu, so probably we end up disabling some menu items
const applyPrivilegesToMenu = (
  menu: IAdminMenuItem[],
  privileges: ModulePrivileges[],
) => {
  for (const item of menu) {
    // If it's a category/parent item -> never disable it (they can't be clicked anyway)
    if (item?.items?.length) {
      applyPrivilegesToMenu(item.items, privileges);
    } else if (item.action) {
      // Action items (e.g. "Make a Wish") aren't privilege-gated.
      item.disabled = false;
    } else {
      if (item.privilege && privileges.includes(item.privilege)) {
        item.disabled = false;
      } else {
        item.disabled = true;
      }
    }
  }
  return menu;
};

export const getFirstAllowedMenuItem = (privilegedMenu: IAdminMenuItem[]) => {
  // Only navigable pages qualify as a redirect target, not action items.
  return findInMenuTree(
    (item) => !item.disabled && !!item.href,
    privilegedMenu,
  );
};

const applyCurrentItemToMenu = (menu: IAdminMenuItem[], location: string) => {
  for (const item of menu) {
    if (
      item.href &&
      (location.endsWith(item.href) || location.startsWith(item.href + '/'))
    ) {
      item.selected = true;
    } else {
      item.selected = false;
    }
    if (item?.items?.length) {
      applyCurrentItemToMenu(item.items, location);
    }
  }
  return menu;
};

export const expandMenuItem = (
  event: TreeViewExpandChangeEvent,
  menu: IAdminMenuItem[],
) => {
  const itemIndex = menu.indexOf(event.item);
  const newTree = structuredClone(menu);
  newTree[itemIndex] = {
    ...newTree[itemIndex],
    expanded: !newTree[itemIndex].expanded,
  };
  return newTree;
};

export const getPrivilegedCurrentMenu = (
  privileges: ModulePrivileges[],
  location: string,
) => {
  const clonedMenu = structuredClone(menu);
  const privilegedMenu = applyPrivilegesToMenu(clonedMenu, privileges);
  const privilegedCurrentMenu = applyCurrentItemToMenu(
    privilegedMenu,
    location,
  );
  return privilegedCurrentMenu;
};

export const findInMenuTree = (
  predicate: (item: IAdminMenuItem) => boolean | undefined,
  tree: IAdminMenuItem[],
): IAdminMenuItem | null => {
  for (const item of tree) {
    if (item?.items?.length) {
      const childResult = findInMenuTree(predicate, item.items);
      if (childResult !== null) return childResult;
    } else {
      if (predicate(item)) return item;
    }
  }
  return null;
};
